import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

import React, { useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { motion, useMotionValue } from 'framer-motion';
import { theme, whenHovered } from "../../theme";
import { easeCurves } from "../../motion/eases";
import { clamp } from '@popmotion/popcorn';
var SIZE = 8;
var GAP = 2;
var HANDLE_SIZE = 48;
export function Resizer(_ref) {
  var isPanning = _ref.isPanning,
      placement = _ref.placement,
      style = _ref.style,
      other = _objectWithoutProperties(_ref, ["isPanning", "placement", "style"]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      hovering = _useState2[0],
      setHovering = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      mouseDown = _useState4[0],
      setOnMouseDown = _useState4[1];

  var boundsRef = useRef(null);
  var xHandle = useMotionValue(0);
  var yHandle = useMotionValue(0);
  var resizerRef = useRef(null);
  var resizerBounds = useRef(null);
  var isVertical = placement === 'left' || placement === 'right';
  var handleMouseEnter = useCallback(function (e) {
    if (!resizerRef.current) {
      return;
    }

    boundsRef.current = e.currentTarget.getBoundingClientRect();
    resizerBounds.current = resizerRef.current.getBoundingClientRect();
    setHovering(true);
  }, []);
  var handleMouseOver = useCallback(function (e) {
    var offset = HANDLE_SIZE / 2;
    var x = e.clientX - boundsRef.current.left;
    var y = e.clientY - boundsRef.current.top;
    xHandle.set(clamp(0, resizerBounds.current.width - offset, x - offset));
    yHandle.set(clamp(0, resizerBounds.current.height - HANDLE_SIZE, y - offset));
  }, [xHandle, yHandle]);
  var handleMouseLeave = useCallback(function () {
    setOnMouseDown(false);
    setHovering(false);
  }, []);
  var onMouseDown = useCallback(function (e) {
    e.preventDefault();
    setOnMouseDown(true);
  }, []);
  var onMouseUp = useCallback(function () {
    setOnMouseDown(false);
  }, []);
  var handlePosition = isVertical ? {
    y: yHandle
  } : {
    x: xHandle
  };
  return /*#__PURE__*/React.createElement(StyledResizer, _extends({}, other, {
    style: _objectSpread({
      opacity: isPanning || hovering ? 1 : 0
    }, style),
    ref: resizerRef,
    placement: placement,
    onMouseDown: onMouseDown,
    onMouseUp: onMouseUp,
    onMouseEnter: handleMouseEnter,
    onMouseMove: !isPanning && !mouseDown ? handleMouseOver : null,
    onMouseLeave: handleMouseLeave,
    isPanning: isPanning || mouseDown
  }), /*#__PURE__*/React.createElement(Handle, {
    style: handlePosition
  }));
}
var positionStyles = {
  right: css(["top:", "px;right:-", "px;width:", "px;bottom:", "px;cursor:col-resize;"], GAP, SIZE / 2, SIZE, GAP),
  left: css(["top:", "px;left:-", "px;width:", "px;bottom:", "px;cursor:col-resize;"], GAP, SIZE / 2, SIZE, GAP),
  top: css(["top:-", "px;left:", "px;right:", "px;height:", "px;cursor:row-resize;"], SIZE / 2, GAP, GAP, SIZE),
  bottom: css(["bottom:-", "px;left:", "px;right:", "px;height:", "px;cursor:row-resize;"], SIZE / 2, GAP, GAP, SIZE)
};
var StyledGripContainer = styled.div.withConfig({
  displayName: "Resizer__StyledGripContainer",
  componentId: "vapor__sc-1ohhc08-0"
})(["display:flex;justify-content:center;position:absolute;top:50%;left:50%;transform:translateX(-50%) translateY(-50%);"]);
export var StyledResizer = styled(motion.div).withConfig({
  displayName: "Resizer__StyledResizer",
  componentId: "vapor__sc-1ohhc08-1"
})(["z-index:", ";box-sizing:border-box;user-select:none;position:absolute;transition:opacity 0.3s ", ";transition-delay:0s;", "{transition-delay:0.2s;}", " ", ""], theme.zIndices.resizer, easeCurves.easeOutQuint, whenHovered, dragIndicator, function (p) {
  return positionStyles[p.placement];
});

function dragIndicator(_ref2) {
  var placement = _ref2.placement;

  switch (placement) {
    case 'left':
    case 'right':
      return css([":before{background:", ";position:absolute;content:'';top:-1px;left:50%;transform:translateX(-50%);bottom:-1px;width:2px;border-radius:10px;}", ":before{opacity:1;}", "{height:", ";width:6px;left:50%;margin-left:-3px;}"], theme.colors.palette.grey15, whenHovered, Handle, theme.spaces[HANDLE_SIZE]);

    case 'top':
    case 'bottom':
      return css([":before{background-color:", ";position:absolute;content:'';left:-1px;top:50%;transform:translateY(-50%);right:-1px;height:2px;border-radius:10px;}", "{width:", ";height:6px;position:absolute;top:50%;margin-top:-3px;}", "{transform:translateX(-50%) translateY(-50%) rotate(90deg);}"], theme.colors.palette.grey15, Handle, theme.spaces[HANDLE_SIZE], StyledGripContainer);

    default:
      throw new Error('Resizer must have a position');
  }
}

var Handle = styled(motion.div).withConfig({
  displayName: "Resizer__Handle",
  componentId: "vapor__sc-1ohhc08-2"
})(["border-radius:10px;background-color:", ";position:absolute;"], theme.colors.palette.brandPrimary);